import { useEffect } from "react";
import type { RailStepKey } from "../rail-model";

/**
 * The moment after a submitted questionnaire (fix-q1flow F47).
 *
 * Submitting q1 used to do nothing visible: the client pressed the button and
 * stayed on the questionnaire they had just finished. This card lands ON the
 * next step, says the finished one is closed, and names where they are now.
 * F33 tone: celebrate what is done, point at what comes next.
 */
export function StepCelebration(props: {
  finished: RailStepKey;
  nextStepTitle: string | null;
  /**
   * Why the client is still here, when they are: "transcripts" = their voice
   * notes are still being written up, "queue" = the answers landed and the
   * next step is not open yet. null = we moved them on. Naming the real
   * reason keeps the card from blaming transcripts that already finished.
   */
  hold: "transcripts" | "queue" | null;
  t: (key: string) => string;
  onDismiss: () => void;
}) {
  // A moment, not a banner that has to be cleaned up: it fades on its own,
  // and the dismiss button is there for anyone who wants it gone sooner.
  useEffect(() => {
    const timer = window.setTimeout(props.onDismiss, 12_000);
    return () => window.clearTimeout(timer);
  }, [props.onDismiss]);

  return (
    <section
      className="step-celebration"
      data-step-celebration={props.finished}
      role="status"
      aria-live="polite"
    >
      <span className="step-celebration-mark" aria-hidden="true">✓</span>
      <div className="step-celebration-copy">
        <strong>{props.t(`celebration.steps.${props.finished}.title`)}</strong>
        <p>
          {props.hold === "transcripts"
            ? props.t(`celebration.steps.${props.finished}.waiting`)
            : props.hold === "queue" || !props.nextStepTitle
              ? props.t(`celebration.steps.${props.finished}.queued`)
              : <>{props.t("celebration.nextLead")} <strong>{props.nextStepTitle}</strong>.</>}
        </p>
      </div>
      <button
        className="quiet-link step-celebration-dismiss"
        type="button"
        onClick={props.onDismiss}
        aria-label={props.t("celebration.dismiss")}
      >
        <span aria-hidden="true">✕</span>
      </button>
    </section>
  );
}
